import { useMemo, useState } from 'react';
import type { UserRow } from '../types';

type Props = {
  rows: UserRow[];
  onSelect: (user: UserRow) => void;
  onSendInvite: (user: UserRow) => void;
  onImpersonate: (user: UserRow) => void;
  currentUserId: number | null;
};

type RoleFilter = 'all' | 'admin' | 'fbc' | 'franchisee';
type SortKey = 'name' | 'afNumber' | 'email' | 'lastLogin';
type SortDir = 'asc' | 'desc';

function initials(name: string | null | undefined, email: string): string {
  const src = name && name.trim().length > 0 ? name.trim() : email;
  const parts = src.split(/[\s.@_-]+/).filter(Boolean);
  return (parts[0]?.[0] ?? '').toUpperCase() + (parts[1]?.[0] ?? '').toUpperCase();
}

const AVATAR_COLOR: Record<UserRow['role'], string> = {
  admin: '#4338ca',
  fbc: '#92400e',
  franchisee: '#0284c7',
};

function statusFor(u: UserRow): { label: string; markerClass: string; pillClass: string } {
  if (u.pulseStatus === 'active') return { label: 'Active', markerClass: 'green', pillClass: 'green' };
  if (u.pulseStatus === 'pending') return { label: 'Pending', markerClass: 'yellow', pillClass: 'yellow' };
  return { label: 'Unregistered', markerClass: 'neutral', pillClass: 'neutral' };
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Tooltip body for the role filter chips: active/pending split, with the
// unregistered tail shown only when present (so the numbers reconcile with the
// chip's total instead of silently dropping not-yet-invited accounts).
function breakdownTitle(b: { active: number; pending: number; unregistered: number }): string {
  let t = `Active: ${b.active} | Pending: ${b.pending}`;
  if (b.unregistered > 0) t += ` | Unregistered: ${b.unregistered}`;
  return t;
}

export default function UsersTable({
  rows,
  onSelect,
  onSendInvite,
  onImpersonate,
  currentUserId,
}: Props) {
  const [filter, setFilter] = useState<RoleFilter>('all');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  function afSortValue(af: string | null | undefined): number {
    if (!af) return Number.POSITIVE_INFINITY;
    const first = af.split(/[,\s]+/)[0];
    const m = first.match(/AF-(\d+)(?:\.(\d+))?/i);
    if (!m) return Number.POSITIVE_INFINITY;
    return Number(m[1]) * 1000 + Number(m[2] ?? 0);
  }

  const counts = useMemo(() => {
    const blank = () => ({ total: 0, active: 0, pending: 0, unregistered: 0 });
    const c = { all: blank(), admin: blank(), fbc: blank(), franchisee: blank() };
    for (const r of rows) {
      const bucket =
        r.pulseStatus === 'active'
          ? 'active'
          : r.pulseStatus === 'pending'
            ? 'pending'
            : 'unregistered';
      c.all.total++;
      c.all[bucket]++;
      c[r.role].total++;
      c[r.role][bucket]++;
    }
    return c;
  }, [rows]);

  const visible = useMemo(() => {
    const q = search.toLowerCase().trim();
    const filtered = rows.filter((r) => {
      if (filter !== 'all' && r.role !== filter) return false;
      if (!q) return true;
      const name = (r.name ?? '').toLowerCase();
      const email = (r.email ?? '').toLowerCase();
      const af = (r.afNumber ?? '').toLowerCase();
      return name.includes(q) || email.includes(q) || af.includes(q);
    });

    if (!sortKey) return filtered;

    const sorted = [...filtered].sort((a, b) => {
      let av: string | number;
      let bv: string | number;
      if (sortKey === 'afNumber') {
        av = afSortValue(a.afNumber);
        bv = afSortValue(b.afNumber);
      } else if (sortKey === 'lastLogin') {
        av = a.lastLogin ? new Date(a.lastLogin).getTime() : 0;
        bv = b.lastLogin ? new Date(b.lastLogin).getTime() : 0;
      } else {
        av = (a[sortKey] ?? '').toString().toLowerCase();
        bv = (b[sortKey] ?? '').toString().toLowerCase();
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [rows, filter, search, sortKey, sortDir]);

  function sortIndicator(key: SortKey): string {
    if (sortKey !== key) return '';
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  }

  return (
    <>
      <div className="ds-toolbar">
        <div className="ds-toolbar-group">
          <input
            type="text"
            className="ds-search"
            placeholder="Search by name, email, or AF#"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="ds-toolbar-group">
          <FilterChip label="All" value="all" active={filter} count={counts.all.total} title={breakdownTitle(counts.all)} onClick={setFilter} />
          <FilterChip label="Admin" value="admin" active={filter} count={counts.admin.total} title={breakdownTitle(counts.admin)} onClick={setFilter} />
          <FilterChip label="FBC" value="fbc" active={filter} count={counts.fbc.total} title={breakdownTitle(counts.fbc)} onClick={setFilter} />
          <FilterChip label="Franchisee" value="franchisee" active={filter} count={counts.franchisee.total} title={breakdownTitle(counts.franchisee)} onClick={setFilter} />
        </div>
      </div>

      <div className="ds-panel">
        {/* The panel clips overflow to keep its rounded corners, so the table
            needs its own scroll container — otherwise the right-most column
            (Actions, including Impersonate) is unreachable on narrow screens. */}
        <div className="ds-table-scroll">
        <table className="ds-table ds-table-sticky-actions">
          <thead>
            <tr>
              <th style={{ width: 32 }}></th>
              <th
                onClick={() => toggleSort('name')}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                User{sortIndicator('name')}
              </th>
              <th
                onClick={() => toggleSort('afNumber')}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                AF{sortIndicator('afNumber')}
              </th>
              <th
                onClick={() => toggleSort('email')}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Email{sortIndicator('email')}
              </th>
              <th>Role</th>
              <th>Status</th>
              <th
                onClick={() => toggleSort('lastLogin')}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Last Login{sortIndicator('lastLogin')}
              </th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', color: '#8a95a5' }}>
                  No users match
                </td>
              </tr>
            ) : (
              visible.map((u) => {
                const s = statusFor(u);
                const ini = initials(u.name, u.email);
                const avatarColor = AVATAR_COLOR[u.role];
                return (
                  <tr key={`${u.id ?? u.email}`} onClick={() => onSelect(u)}>
                    <td>
                      <span className={`ds-marker ${s.markerClass}`} />
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div
                          className="user-avatar"
                          style={{ background: `${avatarColor}14`, color: avatarColor }}
                        >
                          {ini}
                        </div>
                        <div>
                          <div className="ds-name">{u.name || '—'}</div>
                          {u.location && <div className="ds-sub">{u.location}</div>}
                        </div>
                      </div>
                    </td>
                    <td style={{ fontSize: 12, color: '#5a6a7a', whiteSpace: 'nowrap' }}>
                      {u.afNumber ? (
                        <span className="user-af-cell">{u.afNumber}</span>
                      ) : (
                        <span style={{ color: '#b0b8c4' }}>—</span>
                      )}
                    </td>
                    <td style={{ fontSize: 12, color: '#5a6a7a' }}>{u.email}</td>
                    <td>
                      <span className={`ds-role ${u.role}`}>{u.role}</span>
                    </td>
                    <td>
                      <span className={`pill-status ${s.pillClass}`}>
                        <span className="dot" /> {s.label}
                      </span>
                    </td>
                    <td style={{ fontSize: 12, color: '#5a6a7a' }}>
                      {u.lastLogin ? (
                        formatDate(u.lastLogin)
                      ) : (
                        <span style={{ color: '#b0b8c4' }}>Never</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {(u.pulseStatus === 'not_registered' || u.pulseStatus === 'pending') && (
                        <button
                          type="button"
                          className="ds-btn ds-btn-ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            onSendInvite(u);
                          }}
                        >
                          Send Invite
                        </button>
                      )}
                      {u.id !== null &&
                        u.id !== currentUserId &&
                        u.pulseStatus !== 'not_registered' && (
                          <button
                            type="button"
                            className="ds-btn ds-btn-ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              onImpersonate(u);
                            }}
                          >
                            Impersonate
                          </button>
                        )}
                      <button
                        type="button"
                        className="ds-btn ds-btn-ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelect(u);
                        }}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        </div>
      </div>
    </>
  );
}

function FilterChip({
  label,
  value,
  active,
  count,
  title,
  onClick,
}: {
  label: string;
  value: RoleFilter;
  active: RoleFilter;
  count: number;
  title?: string;
  onClick: (v: RoleFilter) => void;
}) {
  return (
    <button
      type="button"
      title={title}
      className={`ds-chip ${active === value ? 'active' : ''}`}
      onClick={() => onClick(value)}
    >
      {label} <span className="count">{count}</span>
    </button>
  );
}
